import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { AddTabModal, 
  // DxfCanvasViewer
 } from 'processhub-components-lib';
import DynamicTabs from './DynamicTabs';
import allHooks from 'hooks';
import { dxfCanvasViewerConfig } from 'config/dxfCanvasViewerConfig';
import { DxfWidgetsProvider } from 'pages/sld/hooks/useDxfWidgets';
import NewDashboard from 'pages/NewDashboard';
import useApp from 'hooks/useApp';
import { FiSettings, FiCheck } from 'react-icons/fi';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import GeneralOverview from 'components/StaticDashboard/GeneralOverview/GeneralOverview';
import Asas1DC from 'components/StaticDashboard/Asas1DC/Asas1DC';
import PowerSource from 'components/StaticDashboard/PowerSource/PowerSource';
import V1UPS from 'components/StaticDashboard/UPS/V1-UPS/V1-UPS';
import ExecutiveDashboard from 'components/StaticDashboard/ExecutiveDashboard/ExecutiveDashboard';
import DxfCanvasViewer from 'pages/sld/DxfCanvasViewer';

const TabsDashboard: React.FC = () => {
  const {
    tabsData,
    removeTab,
    updateTabData,
    activeTabId,
    setActiveTabId,
  } = useApp();

  const [isAddModalOpen, setAddModalOpen] = React.useState(false);
  const [editTabId, setEditTabId] = React.useState<string | null>(null);
  const [deleteTabId, setDeleteTabId] = React.useState<string | null>(null);
  const [configMode, setConfigMode] = React.useState(false);

  // Ensure body remains interactive after modals/actions
  React.useEffect(() => {
    const ensureBodyInteractive = () => {
      const body = document.body as HTMLBodyElement | null;
      if (!body) return;
      if (body.style.pointerEvents === 'none') {
        body.style.pointerEvents = 'auto';
      }
    };
    ensureBodyInteractive();
    return ensureBodyInteractive;
  }, [isAddModalOpen, editTabId, deleteTabId, configMode]);

  const renderContent = (tab: any) => {
    if (!tab) return null;

    switch (tab.type) {
      case 'dxf-viewer':
        return (
          <DxfWidgetsProvider>
            <DxfCanvasViewer forceConfig={configMode && tab.id === activeTabId} dxfCanvasViewerConfig={dxfCanvasViewerConfig} hooks={allHooks} colorTheme='sdaia' />
          </DxfWidgetsProvider>
        );
      case 'widget':
        return <NewDashboard />;
      case 'dashboard':
      default:
        return <NewDashboard />;
    }
  };

  const dynamicTabs = tabsData.map((tab: any) => ({
    id: tab.id,
    label: tab.id === 'overview' ? 'User Overview' : tab.label,
    content: renderContent(tab),
  }));

  const readyTabs = [
    {
      id: 'executive',
      label: 'Executive',
      content: <ExecutiveDashboard />,
    },
    {
      id: 'generalOverview',
      label: 'Overview',
      content: <GeneralOverview />
    },
    {
      id: 'powerSource',
      label: 'Power Source',
      content: <PowerSource />
    },
    {
      id: 'v1-ups',
      label: 'V1-UPS',
      content: <V1UPS type='v1' />
    },
    {
      id: 'v2-ups',
      label: 'V2-UPS',
      content: <V1UPS type='v2' />
    },
    {
      id: 'asas1Dc',
      label: 'ASAS1-DC',
      content: <Asas1DC />
    },
    {
      id: 'asas2Dc',
      label: 'ASAS2-DC',
      content: <Asas1DC />
    },
    {
      id: 'v1dc',
      label: 'V1-DC',
      content: <Asas1DC />
    },
    {
      id: 'v2dc',
      label: 'V2-DC',
      content: <Asas1DC />
    },
    ...dynamicTabs
  ];

  return (
    <div className="bg-black h-screen max-h-screen">
      {/* Config toggle removed to rely on side menu */}
      <div className="space-y-4 h-full">
        <div className="bg-dark rounded-md h-full">
          <div className="flex items-center h-full outline">
            <DynamicTabs
              tabs={readyTabs}
              variant="underline"
              onAddTab={() => setAddModalOpen(true)}
              showAddButton={configMode}
              showTabActions={configMode}
              onDeleteTab={(id) => setDeleteTabId(id)}
              onEditTab={(id) => setEditTabId(id)}
              activeId={activeTabId || undefined}
              onTabChange={(id) => { setActiveTabId(id); }}
              className='sonic-tabs'
              extraButton={
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        className="p-2 text-white hover:text-blue transition-colors"
                        onClick={() => setConfigMode(prev => !prev)}
                      >
                        {configMode ? <FiCheck size={20} /> : <FiSettings size={20} />}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" sideOffset={5} variant="dark">
                      {configMode ? 'Save configuration' : 'Tab configuration'}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              }
            />
          </div>
        </div>
      </div>

      {/* Add new tab */}
      <AddTabModal
        isOpen={isAddModalOpen}
        onClose={() => setAddModalOpen(false)}
        onCreateTab={(_, __, ___) => { }}
        hooks={allHooks}
      />

      {/* Edit tab */}
      {editTabId && (
        <AddTabModal
          isOpen={true}
          mode="edit"
          hooks={allHooks}
          initialTab={{
            name: tabsData.find((tb: any) => tb.id === editTabId)?.label || '',
            type: tabsData.find((tb: any) => tb.id === editTabId)?.type || 'widget'
          }}
          onClose={() => setEditTabId(null)}
          onCreateTab={(name, type, layout) => {
            const tab = tabsData.find((tb: any) => tb.id === editTabId);
            if (!tab) return;
            const shouldClear = type !== tab.type;
            let newData = tab.data;
            if (type === 'widget') {
              try {
                const parsed = tab.data ? JSON.parse(tab.data) : {};
                if (layout) parsed.selectedType = layout;
                newData = JSON.stringify(parsed);
              } catch {
                // fallback keep existing
              }
            }
            updateTabData(editTabId, {
              label: name,
              type,
              data: shouldClear ? '' : newData,
            });
          }}
        />
      )}

      {/* Delete confirmation */}
      {deleteTabId && (
        <Dialog.Root open={true} onOpenChange={(open) => { if (!open) setDeleteTabId(null); }}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/60" />
            <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#1B1B1B] p-6 rounded-md border border-lightGray w-[300px] text-white z-50">
              <Dialog.Title className="mb-4 text-lg">Delete Tab?</Dialog.Title>
              <p className="mb-6 text-sm text-gray-300">Are you sure you want to delete this tab?</p>
              <div className="flex justify-end gap-3">
                <button className="btn-sec" onClick={() => setDeleteTabId(null)}>No</button>
                <button className="btn-danger" onClick={async () => { await removeTab(deleteTabId); setDeleteTabId(null); }}>Yes</button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
};

export default TabsDashboard; 